import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Camera, X, FileCheck2, Eraser } from "lucide-react";
import { toast } from "sonner";

function SignatureCanvas({ value, onChange, testid }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);

  function getCtx() { return ref.current?.getContext("2d"); }
  function pos(e) {
    const rect = ref.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t?.clientX ?? e.clientX) - rect.left;
    const y = (t?.clientY ?? e.clientY) - rect.top;
    return { x, y };
  }
  function start(e) { e.preventDefault(); setDrawing(true); const {x,y} = pos(e); const c = getCtx(); c.beginPath(); c.moveTo(x,y); }
  function move(e) { if(!drawing) return; e.preventDefault(); const {x,y}=pos(e); const c=getCtx(); c.lineTo(x,y); c.strokeStyle = "#60a5fa"; c.lineWidth = 2.5; c.lineCap="round"; c.stroke(); }
  function end() { setDrawing(false); onChange(ref.current.toDataURL("image/png")); }
  function clear() { const c=getCtx(); c.clearRect(0,0,ref.current.width,ref.current.height); onChange(""); }

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight;
  }, []);

  return (
    <div className="space-y-2">
      <canvas ref={ref} className="sig-canvas border border-border/60" data-testid={testid}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={clear}><Eraser className="w-3 h-3 mr-1" />Löschen</Button>
    </div>
  );
}

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const emptyForm = { job_id: "", notes: "", checklist: [{label:"Arbeit erledigt",checked:false},{label:"Material geliefert",checked:false},{label:"Kunde zufrieden",checked:false}], photos: [], signature: "" };
  const [form, setForm] = useState(emptyForm);

  async function load() {
    const [r, j] = await Promise.all([api.get("/reports"), api.get("/jobs")]);
    setReports(r.data); setJobs(j.data);
  }
  useEffect(() => { load(); }, []);

  function addPhoto(e) {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map(f => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = (ev) => res(ev.target.result);
      reader.readAsDataURL(f);
    }))).then(urls => setForm(f => ({ ...f, photos: [...f.photos, ...urls] })));
  }

  async function save() {
    if (!form.job_id) { toast.error("Einsatz auswählen"); return; }
    try {
      await api.post("/reports", form);
      toast.success("Bericht gespeichert");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Dokumentation</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Berichte</h1>
        </div>
        <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-11 px-6 font-semibold" data-testid="btn-new-report"><Plus className="w-4 h-4 mr-1" /> Neuer Bericht</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-xl max-h-[90vh] overflow-auto">
            <DialogHeader><DialogTitle>Einsatzbericht erstellen</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Einsatz</Label>
                <Select value={form.job_id} onValueChange={v=>setForm({...form, job_id:v})}>
                  <SelectTrigger className="rounded-full h-10 px-4" data-testid="report-job"><SelectValue placeholder="Einsatz wählen"/></SelectTrigger>
                  <SelectContent>{jobs.map(j=><SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Checkliste</Label>
                <div className="space-y-2 mt-2">
                  {form.checklist.map((c,i) => (
                    <label key={c.label || `check-${i}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-secondary/60">
                      <Checkbox checked={c.checked} onCheckedChange={(v)=>{
                        const list = [...form.checklist]; list[i].checked = !!v; setForm({...form, checklist: list});
                      }} data-testid={`check-${i}`} />
                      <span className="text-sm">{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div><Label>Notizen</Label>
                <Textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} rows={4} className="rounded-2xl" data-testid="report-notes" />
              </div>
              <div><Label>Fotos</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 cursor-pointer text-sm">
                    <Camera className="w-4 h-4" /> Fotos hinzufügen
                    <input type="file" accept="image/*" multiple hidden onChange={addPhoto} data-testid="report-photos" />
                  </label>
                  <span className="text-xs text-muted-foreground">{form.photos.length} ausgewählt</span>
                </div>
                {form.photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {form.photos.map((p,i) => (
                      <div key={`${p.slice(0,32)}-${i}`} className="relative rounded-xl overflow-hidden">
                        <img src={p} alt="" className="w-full h-24 object-cover" />
                        <button onClick={()=>setForm(f=>({...f, photos: f.photos.filter((_,j)=>j!==i)}))}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-background/80 flex items-center justify-center"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div><Label>Unterschrift</Label>
                <SignatureCanvas value={form.signature} onChange={(v)=>setForm({...form, signature:v})} testid="report-signature" />
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full" data-testid="report-save">Speichern</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reports.map(r => {
          const job = jobs.find(j => j.id === r.job_id);
          return (
            <Card key={r.id} className="rounded-3xl p-6 border-border/60 glass hover:-translate-y-1 transition cursor-pointer"
                  onClick={()=>setViewing(r)} data-testid={`report-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-accent/15 text-accent flex items-center justify-center"><FileCheck2 className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{job?.title || "Einsatz"}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString('de-DE')}</div>
                </div>
              </div>
              {r.notes && <p className="text-sm mt-3 text-muted-foreground line-clamp-2">{r.notes}</p>}
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                <span>{r.photos?.length || 0} Foto(s)</span>
                {r.signature && <span className="text-accent">✓ Signiert</span>}
              </div>
            </Card>
          );
        })}
        {reports.length === 0 && <div className="col-span-full text-center text-muted-foreground py-12">Noch keine Berichte.</div>}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o)=>!o && setViewing(null)}>
        <DialogContent className="rounded-3xl max-w-xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Bericht Details</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="space-y-1">
                {(viewing.checklist||[]).map((c,i)=>(<div key={c.label || `v-check-${i}`} className="flex items-center gap-2 text-sm"><Checkbox checked={c.checked} disabled /> {c.label}</div>))}
              </div>
              {viewing.notes && <div className="rounded-2xl bg-secondary/50 p-3 text-sm">{viewing.notes}</div>}
              {viewing.photos?.length > 0 && (
                <div className="grid grid-cols-2 gap-2">{viewing.photos.map((p,i)=><img key={`${p.slice(0,32)}-${i}`} src={p} alt="" className="rounded-xl w-full" />)}</div>
              )}
              {viewing.signature && (
                <div><Label>Unterschrift</Label><img src={viewing.signature} alt="sig" className="rounded-2xl bg-muted max-h-40" /></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
